import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Divider, Drawer, IconButton, MenuItem, Paper, Stack, Tab, Tabs, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme,
} from '@mui/material';
import { Close, ContentCopy, OpenInNew, VisibilityOutlined } from '@mui/icons-material';
import { DataGrid, GridColDef, type DataGridProps } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker, type DateValidationError } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { adminDataGridSx } from '../../components/ui/adminDataGridSx';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { opsEntityPath } from '../../utils/opsEntityNav';
import { getPaymentTransactions, type PaymentTransaction } from '../../services/business/paymentTransactionService';

const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';
const DISPLAY_DATETIME_FORMAT = 'MM/DD/YYYY hh:mm:ss A';
const PAGE_SIZE = 20;

const METHODS = [
  { key: 'all', label: 'All' },
  { key: 'cash', label: 'Cash' },
  { key: 'wallet', label: 'Wallet' },
  { key: 'card', label: 'Card / Online' },
] as const;

const BUCKET_LABEL: Record<string, string> = { cash: 'Cash', wallet: 'Wallet', card: 'Card / Online' };

const SERVICES = [
  { key: 'transport', label: 'Transport' },
  { key: 'food', label: 'Food' },
  { key: 'grocery', label: 'Grocery' },
  { key: 'porter', label: 'Porter' },
  { key: 'on_demand', label: 'Home Services' },
];

const ORDER_STATUS_FILTERS = [
  { key: 'completed', label: 'Completed / Delivered' },
  { key: 'active', label: 'In Progress' },
  { key: 'pending', label: 'Pending / Searching' },
  { key: 'cancelled', label: 'Cancelled' },
];

const PAYMENT_STATE_FILTERS = [
  { key: 'paid', label: 'Paid' },
  { key: 'pending', label: 'Pending (Cash Due / Awaiting)' },
  { key: 'refunded', label: 'Refunded (full or partial)' },
  { key: 'failed', label: 'Failed' },
  { key: 'not_collected', label: 'Cancelled – Not Collected' },
  { key: 'captured', label: 'Cancelled – Captured, Not Refunded' },
];

const PAYMENT_STATE_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  paid: 'success',
  pending: 'warning',
  processing: 'info',
  refunded: 'info',
  partially_refunded: 'info',
  failed: 'error',
  voided: 'default',
  not_collected: 'default',
  captured: 'error',
};

const ORDER_GROUP_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  completed: 'success',
  active: 'info',
  pending: 'warning',
  cancelled: 'error',
  other: 'default',
};

const QUICK_RANGES = ['Today', 'Last 7 days', 'Last 30 days', 'This Month', 'All time'] as const;
type QuickRange = typeof QUICK_RANGES[number];

function quickRangeDates(range: QuickRange) {
  const today = dayjs();
  const fmt = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD');
  switch (range) {
    case 'Today': return { from: fmt(today), to: fmt(today) };
    case 'Last 7 days': return { from: fmt(today.subtract(6, 'day')), to: fmt(today) };
    case 'Last 30 days': return { from: fmt(today.subtract(29, 'day')), to: fmt(today) };
    case 'This Month': return { from: fmt(today.startOf('month')), to: fmt(today) };
    default: return { from: '', to: '' };
  }
}

function pickerError(field: 'from' | 'to', error: DateValidationError | null) {
  if (!error) return undefined;
  if (error === 'invalidDate') return `Enter a valid date (${DISPLAY_DATE_FORMAT})`;
  if (error === 'disableFuture') return 'Date cannot be in the future';
  if (field === 'from' && error === 'maxDate') return 'From cannot be after To';
  if (field === 'to' && error === 'minDate') return 'To cannot be before From';
  return 'Invalid date';
}

/** Dashboard links may pass full ISO timestamps; the filters work on calendar days. */
function toDateParam(value: string | null) {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
}

function formatDateTime(value?: string) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format(DISPLAY_DATETIME_FORMAT) : value;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <Tooltip title={copied ? 'Copied' : 'Copy'}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          });
        }}
        aria-label="Copy"
      >
        <ContentCopy sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 150, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ minWidth: 0, flex: 1, wordBreak: 'break-word' }}>{children}</Box>
    </Stack>
  );
}

export default function PaymentTransactionsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatAmount } = useTenantCurrency();

  const method = searchParams.get('method') || 'all';
  const service = searchParams.get('service') || '';
  const orderStatus = searchParams.get('orderStatus') || '';
  const paymentState = searchParams.get('paymentState') || '';
  const fromDate = toDateParam(searchParams.get('from'));
  const toDate = toDateParam(searchParams.get('to'));
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<PaymentTransaction | null>(null);
  const [fromPickerError, setFromPickerError] = useState<DateValidationError | null>(null);
  const [toPickerError, setToPickerError] = useState<DateValidationError | null>(null);

  const setParam = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => (v && v !== 'all' ? next.set(k, v) : next.delete(k)));
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const rangeOrderError = fromDate && toDate && toDate < fromDate ? 'To cannot be before From' : undefined;
  const fromError = pickerError('from', fromPickerError) ?? rangeOrderError;
  const toError = pickerError('to', toPickerError) ?? rangeOrderError;
  const selectedRange = QUICK_RANGES.find((r) => {
    const d = quickRangeDates(r);
    return d.from === fromDate && d.to === toDate;
  }) ?? null;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';

  const params = {
    method: method === 'all' ? undefined : method,
    service: service || undefined,
    orderStatus: orderStatus || undefined,
    paymentState: paymentState || undefined,
    search: search.trim() || undefined,
    from: fromDate || undefined,
    to: toDate || undefined,
    timezone,
    page: page + 1,
    limit: PAGE_SIZE,
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['payment-transactions', params],
    queryFn: () => getPaymentTransactions(params),
    enabled: !fromError && !toError,
    placeholderData: keepPreviousData,
  });

  const lastTotal = useRef(0);
  if (data?.meta?.total != null) lastTotal.current = data.meta.total;
  const rowCount = data?.meta?.total ?? lastTotal.current;
  const summary = data?.meta?.summary;

  const rows = useMemo(
    () => (data?.data ?? []).map((r, i) => ({ ...r, serialNo: page * PAGE_SIZE + i + 1 })),
    [data, page],
  );

  const columns: GridColDef<PaymentTransaction & { serialNo: number }>[] = useMemo(() => [
    { field: 'serialNo', headerName: 'S.No', width: 70, sortable: false },
    {
      field: 'createdAt',
      headerName: 'Date & Time',
      width: 150,
      renderCell: (p) => (
        <Tooltip title={formatDateTime(p.value as string)}>
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2">{p.value ? dayjs(p.value as string).format(DISPLAY_DATE_FORMAT) : '—'}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{p.value ? dayjs(p.value as string).format('hh:mm:ss A') : ''}</Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'reference',
      headerName: 'Booking / Order ID',
      minWidth: 200,
      flex: 1,
      renderCell: (p) => (
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0, width: '100%' }}>
          <Tooltip title={p.row.reference}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{p.row.reference}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">{p.row.service}</Typography>
            </Box>
          </Tooltip>
          <CopyButton value={p.row.reference} />
        </Stack>
      ),
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      minWidth: 170,
      flex: 0.8,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ minWidth: 0, py: 0.5 }}>
          <Typography variant="body2" noWrap>{p.row.customerName}</Typography>
          {p.row.customerContact && <Typography variant="caption" color="text.secondary" noWrap display="block">{p.row.customerContact}</Typography>}
        </Box>
      ),
    },
    {
      field: 'paymentMethodLabel',
      headerName: 'Payment Method',
      width: 170,
      renderCell: (p) => (
        <Box sx={{ py: 0.5 }}>
          <Chip size="small" label={BUCKET_LABEL[p.row.paymentBucket] || p.row.paymentMethodLabel} sx={{ height: 20 }} />
          {BUCKET_LABEL[p.row.paymentBucket] !== p.row.paymentMethodLabel && (
            <Typography variant="caption" color="text.secondary" display="block">{p.row.paymentMethodLabel}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'transactionRef',
      headerName: 'Transaction Ref',
      minWidth: 190,
      flex: 0.9,
      sortable: false,
      renderCell: (p) => (p.row.transactionRef ? (
        <Tooltip title={`${p.row.transactionRefSource}: ${p.row.transactionRef}`}>
          <Box sx={{ minWidth: 0, py: 0.5 }}>
            <Typography variant="body2" fontFamily="monospace" noWrap>{p.row.transactionRef}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">{p.row.transactionRefSource}</Typography>
          </Box>
        </Tooltip>
      ) : (
        <Typography variant="caption" color="text.secondary">{p.row.transactionRefSource}</Typography>
      )),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Box sx={{ py: 0.5, textAlign: 'right', width: '100%' }}>
          <Typography variant="body2" fontWeight={700}>{formatAmount(Number(p.row.amount || 0))}</Typography>
          {p.row.refundAmount > 0 && (
            <Typography variant="caption" color="info.main" display="block">− {formatAmount(p.row.refundAmount)} refunded</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'paymentState',
      headerName: 'Payment Status',
      width: 200,
      renderCell: (p) => (
        <Tooltip title={p.row.paymentStateHint}>
          <Chip size="small" label={p.row.paymentStateLabel} color={PAYMENT_STATE_COLOR[p.row.paymentState] || 'default'} />
        </Tooltip>
      ),
    },
    {
      field: 'orderStatus',
      headerName: 'Order / Trip Status',
      width: 170,
      renderCell: (p) => (
        <Chip size="small" variant="outlined" label={p.row.orderStatusLabel} color={ORDER_GROUP_COLOR[p.row.orderStatusGroup] || 'default'} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" startIcon={<VisibilityOutlined fontSize="small" />} onClick={(e) => { e.stopPropagation(); setSelected(p.row); }}>
          View
        </Button>
      ),
    },
  ], [formatAmount]);

  const detailPath = selected ? opsEntityPath(selected as unknown as Record<string, unknown>) : null;
  const activeFilters = [service, orderStatus, paymentState, fromDate, toDate, search.trim()].filter(Boolean).length;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Payment Transactions"
        subtitle="Cash, wallet and card/online payments across Transport, Food, Grocery, Porter and Home Services."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Payment Transactions' },
        ]}
      />

      <Tabs
        value={METHODS.some((m) => m.key === method) ? method : 'all'}
        onChange={(_, v) => setParam({ method: v })}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {METHODS.map((m) => {
          const s = summary?.[m.key as keyof typeof summary];
          return (
            <Tab
              key={m.key}
              value={m.key}
              label={(
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={700}>{m.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s ? `${s.count} · ${formatAmount(s.amount)}` : '—'}
                  </Typography>
                </Box>
              )}
              sx={{ textTransform: 'none', alignItems: 'flex-start' }}
            />
          );
        })}
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ lg: 'flex-start' }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Search"
            placeholder="Booking / order ID or gateway payment ID"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 280, flex: 1 }}
            helperText=" "
          />
          <TextField select size="small" label="Service" value={service} onChange={(e) => setParam({ service: e.target.value })} sx={{ minWidth: 160 }} helperText=" " InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="">All services</MenuItem>
            {SERVICES.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Payment Status" value={paymentState} onChange={(e) => setParam({ paymentState: e.target.value })} sx={{ minWidth: 220 }} helperText=" " InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="">All payment statuses</MenuItem>
            {PAYMENT_STATE_FILTERS.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Order / Trip Status" value={orderStatus} onChange={(e) => setParam({ orderStatus: e.target.value })} sx={{ minWidth: 200 }} helperText=" " InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="">All order statuses</MenuItem>
            {ORDER_STATUS_FILTERS.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
          </TextField>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              format={DISPLAY_DATE_FORMAT}
              value={fromDate ? dayjs(fromDate) : null}
              maxDate={toDate ? dayjs(toDate) : undefined}
              disableFuture
              onError={setFromPickerError}
              onChange={(v) => setParam({ from: v && v.isValid() ? v.format('YYYY-MM-DD') : '' })}
              slotProps={{ textField: { size: 'small', sx: { width: 170 }, error: !!fromError, helperText: fromError || ' ' }, field: { clearable: true } }}
            />
            <DatePicker
              label="To"
              format={DISPLAY_DATE_FORMAT}
              value={toDate ? dayjs(toDate) : null}
              minDate={fromDate ? dayjs(fromDate) : undefined}
              disableFuture
              onError={setToPickerError}
              onChange={(v) => setParam({ to: v && v.isValid() ? v.format('YYYY-MM-DD') : '' })}
              slotProps={{ textField: { size: 'small', sx: { width: 170 }, error: !!toError, helperText: toError || ' ' }, field: { clearable: true } }}
            />
          </LocalizationProvider>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={selectedRange}
            onChange={(_, v: QuickRange | null) => {
              if (!v) return;
              const d = quickRangeDates(v);
              setFromPickerError(null);
              setToPickerError(null);
              setParam({ from: d.from, to: d.to });
            }}
            sx={{
              flexWrap: 'wrap',
              gap: 0.75,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mx: 0, px: 1.25,
                textTransform: 'none', fontWeight: 650,
              },
              '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
                bgcolor: 'primary.main', borderColor: 'primary.main !important', color: 'primary.contrastText', fontWeight: 800,
              },
            }}
          >
            {QUICK_RANGES.map((r) => <ToggleButton key={r} value={r}>{r}</ToggleButton>)}
          </ToggleButtonGroup>
          {activeFilters > 0 && (
            <Button
              size="small"
              onClick={() => {
                setSearch('');
                setParam({ service: '', orderStatus: '', paymentState: '', from: '', to: '' });
              }}
            >
              Clear filters
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
            {rowCount} transaction{rowCount === 1 ? '' : 's'}{isFetching && data ? ' · updating…' : ''}
          </Typography>
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}>
          Could not load payment transactions. Check your connection and try again.
        </Alert>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        getRowId={(r) => r._id}
        autoHeight
        rowHeight={60}
        disableRowSelectionOnClick
        disableColumnMenu
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={{ page, pageSize: PAGE_SIZE }}
        onPaginationModelChange={(m) => setPage(m.page)}
        pageSizeOptions={[PAGE_SIZE]}
        onRowClick={(p) => setSelected(p.row)}
        localeText={{ noRowsLabel: 'No transactions match these filters' }}
        sx={[adminDataGridSx(theme), { bgcolor: 'background.paper', borderRadius: 2, '& .MuiDataGrid-row': { cursor: 'pointer' } }] as DataGridProps['sx']}
      />

      <Drawer anchor="right" open={!!selected} onClose={() => setSelected(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}>
        {selected && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box>
                <Typography variant="overline" color="text.secondary">{selected.service} payment</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ wordBreak: 'break-all' }}>{selected.reference}</Typography>
              </Box>
              <IconButton onClick={() => setSelected(null)} aria-label="Close"><Close /></IconButton>
            </Stack>
            <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={selected.paymentStateLabel} color={PAYMENT_STATE_COLOR[selected.paymentState] || 'default'} />
              <Chip size="small" variant="outlined" label={`Order: ${selected.orderStatusLabel}`} color={ORDER_GROUP_COLOR[selected.orderStatusGroup] || 'default'} />
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>{selected.paymentStateHint}</Typography>

            <Typography variant="subtitle2" fontWeight={700}>Payment</Typography>
            <Divider sx={{ mb: 1 }} />
            <DetailRow label="Amount"><Typography variant="body2" fontWeight={700}>{formatAmount(selected.amount)} {selected.currency}</Typography></DetailRow>
            {selected.discount > 0 && (
              <DetailRow label="Discount">
                <Typography variant="body2">{formatAmount(selected.discount)}{selected.promoCode ? ` (${selected.promoCode})` : ''}</Typography>
              </DetailRow>
            )}
            <DetailRow label="Method"><Typography variant="body2">{selected.paymentMethodLabel} · {BUCKET_LABEL[selected.paymentBucket]}</Typography></DetailRow>
            <DetailRow label="Transaction Ref">
              {selected.transactionRef ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" fontFamily="monospace">{selected.transactionRef}</Typography>
                  <CopyButton value={selected.transactionRef} />
                </Stack>
              ) : <Typography variant="body2" color="text.secondary">—</Typography>}
              <Typography variant="caption" color="text.secondary" display="block">{selected.transactionRefSource}</Typography>
            </DetailRow>
            {selected.gatewayOrderId && selected.gatewayOrderId !== selected.transactionRef && (
              <DetailRow label="Gateway Order ID"><Typography variant="body2" fontFamily="monospace">{selected.gatewayOrderId}</Typography></DetailRow>
            )}
            <DetailRow label="Payment Status (raw)"><Typography variant="body2">{selected.paymentStatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</Typography></DetailRow>
            {selected.paidAt && <DetailRow label="Paid At"><Typography variant="body2">{formatDateTime(selected.paidAt)}</Typography></DetailRow>}

            <Typography variant="subtitle2" fontWeight={700} mt={2}>Refund</Typography>
            <Divider sx={{ mb: 1 }} />
            {selected.refundAmount > 0 ? (
              <>
                <DetailRow label="Refund Amount"><Typography variant="body2" fontWeight={700}>{formatAmount(selected.refundAmount)}</Typography></DetailRow>
                <DetailRow label="Refunded To">
                  <Typography variant="body2">{selected.refundDestination === 'wallet' ? 'Customer wallet' : 'Original payment method'}</Typography>
                </DetailRow>
                {selected.refundRef && (
                  <DetailRow label="Refund Ref">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" fontFamily="monospace">{selected.refundRef}</Typography>
                      <CopyButton value={selected.refundRef} />
                    </Stack>
                  </DetailRow>
                )}
                {selected.refundedAt && <DetailRow label="Refunded At"><Typography variant="body2">{formatDateTime(selected.refundedAt)}</Typography></DetailRow>}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 0.75 }}>No refund recorded.</Typography>
            )}

            <Typography variant="subtitle2" fontWeight={700} mt={2}>{selected.kind === 'ride' ? 'Trip' : 'Order / Booking'}</Typography>
            <Divider sx={{ mb: 1 }} />
            <DetailRow label="ID">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{selected.reference}</Typography>
                <CopyButton value={selected.reference} />
              </Stack>
            </DetailRow>
            <DetailRow label="Service"><Typography variant="body2">{selected.service}</Typography></DetailRow>
            <DetailRow label="Customer">
              <Typography variant="body2">{selected.customerName}</Typography>
              {selected.customerContact && <Typography variant="caption" color="text.secondary" display="block">{selected.customerContact}</Typography>}
            </DetailRow>
            <DetailRow label="Created"><Typography variant="body2">{formatDateTime(selected.createdAt)}</Typography></DetailRow>
            {selected.orderStatusGroup === 'cancelled' && (
              <>
                {selected.cancelledAt && <DetailRow label="Cancelled At"><Typography variant="body2">{formatDateTime(selected.cancelledAt)}</Typography></DetailRow>}
                <DetailRow label="Cancel Reason"><Typography variant="body2">{selected.cancelReason || '—'}</Typography></DetailRow>
              </>
            )}

            {detailPath && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<OpenInNew />}
                sx={{ mt: 3 }}
                onClick={() => navigate(detailPath, { state: { from: '/payment-transactions' } })}
              >
                Open {selected.kind === 'ride' ? 'trip' : 'order'} details
              </Button>
            )}
          </Box>
        )}
      </Drawer>
    </PageShell>
  );
}
